/**
 * AppRoutes — the app's route tree, extracted out of App.tsx (Wave 3 · B7
 * bottom tabs) so it can be rendered once per open tab by TabsHost, each
 * instance matching against that TAB's own remembered location instead of
 * the single global browser location.
 *
 * `<Routes location={location}>` (react-router v7) matches/renders against
 * whatever `location` is passed in — the ACTIVE tab is handed the live
 * browser location (so normal <Link>/useNavigate calls inside it keep
 * driving the real address bar), an INACTIVE tab is handed its own frozen
 * location snapshot (so it keeps rendering whatever it was last showing
 * without touching the URL). See tabs/TabsHost.tsx for the wiring.
 *
 * When `location` is omitted, <Routes> falls back to the ambient location
 * from the nearest Router context — used by anything that still renders
 * AppRoutes outside the tab system (there is none today, but this keeps the
 * component usable standalone, e.g. in a future test harness).
 *
 * Content, order and behavior of every <Route> below is unchanged from the
 * pre-tabs App.tsx — this is a pure extraction, not a route-table edit.
 */
import { lazy, Suspense, type ReactNode } from 'react'
import { Routes, Route, Navigate, Link, useLocation, useParams, type Location } from 'react-router-dom'
import { Loader2, Compass } from 'lucide-react'
import ErrorBoundary from './components/ErrorBoundary'
import { useAmcStatus } from './lib/useAmcStatus'
import { legacyRedirectTarget, type LegacyRedirectKind } from './pages/automations/routing'
import { ADS_HUB_CUTOVER, LEGACY_ADS_ROUTES, LEGACY_PATH_REDIRECTS, legacyAdsHref } from './pages/ads/legacyRoutes'

// Route pages are lazy-loaded (code-split): each page + its heavy deps
// (recharts on the chart pages, react-querybuilder on the rule wizard) only
// download when that route is first visited, instead of riding in one ~1.4MB
// eager entry chunk. A <Suspense> around <Routes> paints a spinner during the
// per-page chunk fetch.
// Automations port (CONTRACTS 10.1): /rules is the four-tab Automations hub;
// the old five-tab hub is kept at /rules/legacy as "Rules (classic)". The
// rule-engine pages are exported so pages/automations/routes.test.tsx can say
// WHICH page a path renders (lazy() components are still components, so the
// exports cost fast refresh nothing).
export const AutomationsHub = lazy(() => import('./pages/automations/AutomationsHub'))
export const RulesHub = lazy(() => import('./pages/RulesHub'))
export const RulesPreflightPage = lazy(() => import('./pages/RulesPreflight'))
export const RuleBacktestPage = lazy(() => import('./pages/RuleBacktest'))
// Phase 12 Build 38 — describe a recurring job, get a workflow drafted for the
// workflow runner, and see step by step whether ClickPay can actually carry it
// out. Nothing on this page runs anything.
export const WorkflowProposalsPage = lazy(() => import('./pages/WorkflowProposals'))
// RuleDetail (the read-only JSON page) is RETIRED — /rules/:id redirects to
// the rule's Activity (it went to the Runs-scoped view before the Automations
// port). The component file (pages/RuleDetail.tsx) was deleted (A4 papercut
// #3) — no route ever rendered it.
// WS1 (2026-06-15): Dashboard renamed to Command Center, mounted at /overview.
const CommandCenterPage = lazy(() => import('./pages/CommandCenter'))
// WS1: Reports deprecated (route redirects to /overview) — page component
// stays in the tree but is no longer imported/routed here.
export const CampaignsPage = lazy(() => import('./pages/Campaigns'))
export const CampaignBuilderPage = lazy(() => import('./pages/CampaignBuilder'))
// PR 4 — the Advertising hub (/ads), beside the old PPC pages until PR 5.
export const AdsHubPage = lazy(() => import('./pages/ads/AdsHub'))
const AgentsPage = lazy(() => import('./pages/Agents'))
const AIAssistantPage = lazy(() => import('./pages/AIAssistant'))
// The workflow library (Phase 12 Build 37) — the recurring jobs we do,
// written down. Read-only for everyone; admins author in place.
const WorkflowsPage = lazy(() => import('./pages/Workflows'))
const AudiencesPage = lazy(() => import('./pages/Audiences'))
// New AdPro-parity entity pages. v2 #4: Keywords folded into the Targets shell
// as a sub-tab (KeywordsTable), so KeywordsPage is no longer routed here.
export const AdGroupsPage = lazy(() => import('./pages/AdGroups'))
export const TargetsPage = lazy(() => import('./pages/Targets'))
export const SearchTermsPage = lazy(() => import('./pages/SearchTerms'))
// WS1: ProductAds deprecated (route redirects to /products) — page component
// stays in the tree but is no longer imported/routed here.
export const PlacementsPage = lazy(() => import('./pages/Placements'))
export const NegativesPage = lazy(() => import('./pages/Negatives'))
// PR 15: deals and coupons on our products, and how they did.
const DealsPage = lazy(() => import('./pages/Deals'))
// SellerMate-upgrade W5.2: the Ads Overview report; /reports lands here.
const AdsOverviewPage = lazy(() => import('./pages/AdsOverview'))
const SuggestionsPage = lazy(() => import('./pages/Suggestions'))
// SellerMate W4.3: the weekly account audit (score, findings, P1/P2, tables, PDF).
const AuditPage = lazy(() => import('./pages/Audit'))
// SellerMate W3.3 — the business Alerts centre (alert log + saved alerts).
const AlertsPage = lazy(() => import('./pages/Alerts'))
const HourlyPage = lazy(() => import('./pages/Hourly'))
// Budget Pacing page removed outright (2026-07-01; was briefly a Campaigns
// sub-tab) — AMS pacing surfaces as the Pacing column + budget-spent filters
// on /campaigns. /budget-pacing redirects there (see route below).
// SpKeywordHourly folded into Hourly's PPC subtab (F3) — see /hourly redirect below.
const ProductsPage = lazy(() => import('./pages/Products'))
const ProductDetailPage = lazy(() => import('./pages/ProductDetail'))
// #16b — scoped/unscoped view over /api/audit; entry point is the "View
// history" kebab item StateToggle.tsx now renders on every PPC entity row.
const ChangeHistoryPage = lazy(() => import('./pages/ChangeHistory'))
export const PreviewNewRuleV3Page = lazy(() => import('./pages/PreviewNewRuleV3'))
const AutomationEditorPage = lazy(() => import('./pages/automations/editor/AutomationEditorPage'))
const SavedFilterEditorPage = lazy(() => import('./pages/automations/editor/SavedFilterEditorPage'))
// Settings (PR 7; was the Ops Hub, product review #2 + #12): ONE page at /ops
// that opens on Users, with System health and the other engineering views as
// admin tabs. pages/settings/routing.ts holds its tabs and old-URL redirects.
const OpsHub = lazy(() => import('./pages/OpsHub'))
// ClickPay Phase 4 — vendored MasterData ("ops") pages, mounted under
// /inventory/*. Lazy-loaded (MasterData lazy-loaded these in its own App for
// the same reason): keeps the heavy ops subgraph (EntityTable + tanstack
// table, RestockHub's 2.5k-line module, drawers) out of the PPC bundle until
// an /inventory route is visited. OpsLayout scopes MasterData's providers
// (UIPrefs/Toast/KeyboardShortcuts) + Suspense fallback to this subtree.
import OpsLayout from './ops/OpsLayout'
// Marketplaces hub (2026-06-19): the standalone Inventory Products page was
// absorbed into the hub as a sub-tab, so it is no longer routed here. Its
// drawer body now lives in ops/components/ProductEdit.tsx (the old page file
// was deleted in PR 22d). The ProductDetail route stays for /inventory/products/:uid.
const OpsProductDetailPage = lazy(() => import('./ops/pages/ProductDetail'))
// Feedback #118/#119 (2026-09-23): ONE page for every product's settings,
// hidden products included (the UID drawer is only reachable from a row that
// is on the table — a hidden product never is). Sidebar: Inventory → Product
// settings; linked from every product drawer and the "N hidden" links.
const OpsProductSettingsPage = lazy(() => import('./ops/pages/ProductSettings'))
// WS1 (2026-06-15): Suppliers folded into Inventory Products as a sub-tab —
// the Suppliers page was embedded inside the old Products page (deleted in
// PR 22d; /inventory/suppliers redirects away), not routed here. Logistics deprecated (route redirects) — its page component
// stays in the tree but is no longer routed. (DocumentIntake was restored in
// PR-B — its lazy import + route are below.)
const OpsFolderMapPage     = lazy(() => import('./ops/pages/FolderMap'))
// Orders / Shipments / Payments are folded into ONE tabbed shell at
// /inventory/orders (?tab=timeline|shipments|payments). The shell imports
// the three page bodies directly (Timeline = the former Orders PO list), so
// they're no longer lazy-routed individually here. /inventory/shipments and
// /inventory/payments redirect onto the matching sub-tab (below). Same
// page-folding pattern as Campaigns + Inventory Products.
const OpsOrdersTabsPage    = lazy(() => import('./ops/pages/OrdersTabs'))
const OpsRestockHubPage    = lazy(() => import('./ops/pages/RestockHub'))
// Document Intake — the operator receive workspace (drop a packing list /
// invoice → extract line items → reconcile a shipment). Entry point is the
// "Document Intake" button on the Orders tab strip (ReceiveDocsButton).
// PR-B (2026-06-16): UN-DEPRECATED. WS1 had redirected this route to Products
// on a FALSE premise that the receive flow was "embedded" in Orders/Shipments/
// Payments — it never was, which left every orphan shipment unworkable and is
// the root cause of the Orders-tab drift. Restored as the keystone of the
// Orders → shipment → COGS reconciliation.
const OpsReceiveWorkspacePage = lazy(() => import('./ops/pages/ReceiveWorkspace'))
// Kept routed at /inventory/document-receive/legacy while the rebuilt
// workspace beds in — the old page is the only place a combined-PDF
// split or a manual line-item entry can be done today.
const OpsDocumentIntakePage = lazy(() => import('./ops/pages/DocumentIntake'))

/** Dark-launch route gate: /audiences redirects to /overview unless AMC is
 *  enabled (CLICKPAY_AMC on). Renders nothing while status resolves so a
 *  direct hit doesn't flash the page before redirecting. */
function AmcGate({ children }: { children: ReactNode }) {
  const { status, loading } = useAmcStatus()
  if (loading) return null
  if (!status.enabled) return <Navigate to="/overview" replace />
  return <>{children}</>
}

/** Suspense fallback shown while a lazy page chunk downloads. Centered spinner
 *  inside the content area. Mirrors the AuthGate boot splash + OpsLayout's
 *  PageFallback. Exported so TabsHost can reuse the identical fallback per tab. */
export function PageLoading() {
  return (
    <div className="h-full flex items-center justify-center text-ink-faint">
      <Loader2 size={22} className="animate-spin" aria-label="Loading" />
    </div>
  )
}

/**
 * Shown when no route below matches — added with the workflow library (Phase
 * 12 Build 37).
 *
 * Until this existed, an address the route table did not claim rendered
 * NOTHING: <Routes> matched no <Route>, the content area painted empty, and
 * the person was left staring at a blank page with the sidebar still around
 * it. The comment on the /budget-pacing redirect below has said so in as many
 * words for a year ("there is no catch-all route, so removing it would render
 * a blank page").
 *
 * The workflow library is what made that worth fixing rather than noting: a
 * written procedure can name the screen each step happens on, those paths are
 * stored in the database (bbpro_workflow_docs), and screens in this app get
 * renamed and folded into each other regularly — see how many redirects are
 * below. An employee following step 3 of a procedure needs to be told the
 * screen moved. A blank page tells them ClickPay is broken.
 *
 * It deliberately does NOT say "404" or "error": nothing has gone wrong with
 * the app, the address is just no longer a screen.
 */
function ScreenNotFound() {
  const { pathname } = useLocation()
  return (
    <div className="p-8">
      <div className="max-w-xl mx-auto mt-12 text-center">
        <Compass size={22} className="mx-auto text-ink-faint" />
        <h1 className="mt-3 text-lg font-semibold text-ink">
          There is no screen at this address
        </h1>
        <p className="mt-2 text-[13px] leading-relaxed text-ink-secondary">
          ClickPay is working — this particular address just isn’t a screen (any
          more). Screens get renamed and merged as the app changes, so a link
          somebody saved, or a step in a written workflow, can end up pointing
          at one that has moved.
        </p>
        <p className="mt-2 text-xs font-mono text-ink-muted break-all">{pathname}</p>
        <p className="mt-4 text-[13px] text-ink-secondary">
          Pick the screen you wanted from the sidebar, or{' '}
          <Link to="/overview" className="text-accent hover:underline">
            go to the Daily Brief
          </Link>
          . If a workflow step sent you here, tell whoever wrote it down so they
          can point the step somewhere real.
        </p>
      </div>
    </div>
  )
}

/** Wraps the routed pages in an ErrorBoundary keyed to a path, so a render
 *  crash on one page shows a recoverable message (header + sidebar + the tab
 *  bar stay live) and navigating away clears it. Takes an explicit
 *  `resetKey` (rather than reading useLocation itself) so each TABS's
 *  boundary resets on THAT tab's own navigation, not the global browser URL —
 *  an inactive tab's boundary must not reset just because a different tab
 *  navigated. */
export function RouteErrorBoundary({ resetKey, children }: { resetKey: string; children: ReactNode }) {
  return <ErrorBoundary resetKey={resetKey}>{children}</ErrorBoundary>
}

/** Convenience wrapper used by the single (non-tabbed) render path — reads
 *  the live browser location itself. Not used by TabsHost (which passes an
 *  explicit resetKey per tab), kept for any standalone usage. */
export function LiveRouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation()
  return <RouteErrorBoundary resetKey={location.pathname}>{children}</RouteErrorBoundary>
}

/** A retired rules address sent on to where its job lives now. One component
 *  for every such route; the targets are pure functions in
 *  pages/automations/routing.ts (legacyRedirectTarget), where routes.test.tsx
 *  walks them:
 *    - 'runs' / 'approvals' / 'rule': /runs, /approvals and /rules/:id land on
 *      Activity, keeping ?rule_id= and ?run= (the "Last run" / "Fires 7d"
 *      links and the old "inspect run X" deep links); a /runs status list
 *      naming a failure becomes state=failed, and /approvals is the
 *      "Waiting on you" filter.
 *    - 'tags': the Tags tab lives on in Rules (classic).
 *    - 'classic-edit' / 'classic-new': the retired raw-JSON tree editor
 *      (/rules/:id/tree), the flat editor (/rules/:id/edit-legacy) and
 *      /rules/new-legacy open the classic wizard, which still builds trees,
 *      keeping the query (e.g. ?step=2). */
export function LegacyRedirect({ kind }: { kind: LegacyRedirectKind }) {
  const { id } = useParams<{ id: string }>()
  const { search } = useLocation()
  return <Navigate to={legacyRedirectTarget(kind, id, search)} replace />
}

/** An old PPC page sent into the Advertising hub (PR 5), keeping its query.
 *  `replace` hands the transient bottom tab over to /ads (TabsContext), so no
 *  dead "Campaigns" pill is left beside "Advertising". */
export function LegacyAdsRedirect() {
  const { pathname, search, hash } = useLocation()
  const to = legacyAdsHref(pathname, search)
  return to ? <Navigate to={to + hash} replace /> : <ScreenNotFound />
}

/** Redirect the legacy /inventory/shipments and /inventory/payments routes
 *  onto the folded Orders shell's matching sub-tab
 *  (/inventory/orders?tab=shipments|payments), preserving any other query
 *  params the deep link carried so bookmarks/links keep working. */
export function RedirectToOrdersTab({ tab }: { tab: 'shipments' | 'payments' }) {
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  params.set('tab', tab)
  return <Navigate to={`/inventory/orders?${params.toString()}`} replace />
}

/** Redirect /inventory/restock-hub → /inventory/marketplaces (the renamed
 *  Marketplaces hub), preserving any ?mp=/?tab= deep-link params. */
function RedirectToMarketplaces() {
  const { search } = useLocation()
  return <Navigate to={`/inventory/marketplaces${search}`} replace />
}

/** Redirect the absorbed standalone /inventory/products page onto the
 *  Marketplaces hub, mapping its old sub-tab (?tab=catalog|profitability|
 *  suppliers|linking) onto the hub's matching sub-tab id. Other query params
 *  (e.g. a search) are preserved. The hub's SUBTAB_ALIASES also resolves these
 *  ids, so even an un-mapped legacy value lands on the right surface. */
const PRODUCTS_TAB_TO_HUB: Record<string, string> = {
  catalog: 'products',
  // P&L folded into Products (Sales | P&L toggle) — the standalone P&L tab was
  // removed, so Profitability now lands on Products directly.
  profitability: 'products',
  suppliers: 'orders',
  linking: 'listings',
}
function RedirectProductsToMarketplaces() {
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const oldTab = params.get('tab')
  // Map the legacy sub-tab; default (no ?tab=) lands on the hub's default
  // Products sub-tab, so we drop ?tab= entirely in that case.
  const hubTab = oldTab ? (PRODUCTS_TAB_TO_HUB[oldTab] ?? oldTab) : null
  if (hubTab && hubTab !== 'products') params.set('tab', hubTab)
  else params.delete('tab')
  const qs = params.toString()
  return <Navigate to={`/inventory/marketplaces${qs ? `?${qs}` : ''}`} replace />
}

/**
 * The rule-engine routes (CONTRACTS 10.1), as a fragment <Routes> flattens in
 * place. A function rather than inline JSX so pages/automations/routes.test.tsx
 * can feed the SAME elements to createRoutesFromElements + matchRoutes and
 * prove where every path lands. Order is irrelevant: react-router ranks static
 * segments above :params (so /rules/legacy and /rules/preflight beat
 * /rules/:id, and /rules/legacy/new cannot be read as a rule id).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function ruleEngineRoutes() {
  return (
    <>
      {/* The Automations hub: ?tab=automations (default) | activity |
          metrics | filters. The old hub's tab ids still work — the hub
          rewrites ?tab=rules|runs|approvals onto its own tabs and sends
          ?tab=tags|coverage to Rules (classic). */}
      <Route path="/rules" element={<AutomationsHub />} />
      {/* clickpay/rules-preflight: Go-Live Preflight. Static segment —
          react-router ranks it above /rules/:id automatically. */}
      <Route path="/rules/preflight" element={<RulesPreflightPage />} />
      {/* The new automation editor (CONTRACTS 10.3, mounted at integration)
          opens /rules/new and /rules/:id/edit, and the saved-filter editor
          the two /rules/saved-filters routes. The classic V3 wizard, which
          still authors every rule type (decision trees included) and saves
          through /api/rules, stays reachable at /rules/legacy/new and
          /rules/legacy/:id/edit. */}
      <Route path="/rules/new" element={<AutomationEditorPage />} />
      <Route path="/rules/:id/edit" element={<AutomationEditorPage />} />
      <Route path="/rules/saved-filters/new" element={<SavedFilterEditorPage />} />
      <Route path="/rules/saved-filters/:id" element={<SavedFilterEditorPage />} />
      {/* Retired: /rules/:id (the read-only RuleDetail) → the rule's Activity. */}
      <Route path="/rules/:id" element={<LegacyRedirect kind="rule" />} />
      {/* Phase 4 Build 06 — read-only backtest replay. Static suffix, so
          react-router ranks it above the bare /rules/:id redirect the same
          way /rules/:id/edit already does. */}
      <Route path="/rules/:id/backtest" element={<RuleBacktestPage />} />

      {/* Rules (classic): the old five-tab hub (Rules, Runs, Approvals, Tags,
          Automation coverage) and the V3 wizard, kept whole as the fallback
          while the new pages bed in. Deleting them is a later, separate PR. */}
      <Route path="/rules/legacy" element={<RulesHub />} />
      <Route path="/rules/legacy/new" element={<PreviewNewRuleV3Page isPreview={false} />} />
      <Route path="/rules/legacy/:id/edit" element={<PreviewNewRuleV3Page isPreview={false} />} />
      <Route path="/rules/legacy/preflight" element={<RulesPreflightPage />} />

      {/* Retired editors: the raw-JSON tree editor (/rules/:id/tree), the
          flat editor (/rules/:id/edit-legacy) and /rules/new-legacy. The
          classic wizard does all of their jobs. */}
      <Route path="/rules/:id/edit-legacy" element={<LegacyRedirect kind="classic-edit" />} />
      <Route path="/rules/:id/tree" element={<LegacyRedirect kind="classic-edit" />} />
      <Route path="/rules/new-legacy" element={<LegacyRedirect kind="classic-new" />} />

      {/* Folded pages. /runs and /approvals are Activity now (keeping
          ?rule_id= and ?run=; see LegacyRedirect); /tags is a tab of Rules
          (classic). */}
      <Route path="/runs" element={<LegacyRedirect kind="runs" />} />
      <Route path="/approvals" element={<LegacyRedirect kind="approvals" />} />
      <Route path="/tags" element={<LegacyRedirect kind="tags" />} />

      {/* /automation-drafts moved to foldRoutes() (PR 6): reached from
          Automations > New automation. */}

      {/* PREVIEW v3 — Classic 4-step (Pick → Conditions → Action →
          Review) with LIVE preview pane on the right. isPreview defaults to
          true: mock mode, nothing saves. Unchanged by the Automations port. */}
      <Route path="/preview/new-rule-v3" element={<PreviewNewRuleV3Page />} />
    </>
  )
}

/**
 * The routes PR 6 folded into other pages. Nothing is deleted: each old URL
 * still lands somewhere useful. A function, like `ruleEngineRoutes`, so
 * AppRoutes.folds.test.tsx can walk the SAME elements.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function foldRoutes() {
  return (
    <>
      {/* Strategies is "From a strategy" on New campaign; Listing Agent is a
          block in the Listings drawer of the Marketplaces hub. */}
      <Route path="/strategies" element={<Navigate to="/campaigns/new?from=strategy" replace />} />
      <Route path="/listing" element={<Navigate to="/inventory/marketplaces?tab=listings" replace />} />
      {/* Phase 12 Build 38 — automation drafts. Its own top-level path rather
          than a child of /workflows: Build 37's /workflows library is written
          procedure for PEOPLE, this is drafted instruction for the RUNNER, and
          nesting one under the other would say they are the same thing. Reached
          from Automations > New automation (the menu), no longer from the
          sidebar. */}
      <Route path="/automation-drafts" element={<WorkflowProposalsPage />} />
      {/* B22: a bare /shipments is the Shipments tab of Orders & shipments. */}
      <Route path="/shipments" element={<RedirectToOrdersTab tab="shipments" />} />
    </>
  )
}

/**
 * The advertising routes: the Advertising hub (/ads, PR 4) and the old PPC
 * paths. A function, like `ruleEngineRoutes`, so pages/ads/routes.test.tsx can
 * feed the SAME elements to createRoutesFromElements + matchRoutes and prove
 * where each path lands. PR 5 turned the old paths into redirects onto the hub
 * (the table is `LEGACY_ADS_ROUTES`); `cutover: false` mounts the six old
 * pages exactly as before, which is the revert path.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function adsRoutes({ cutover = ADS_HUB_CUTOVER }: { cutover?: boolean } = {}) {
  if (cutover) {
    return (
      <>
        {/* PR 4 — one page for every PPC level: ?tab=campaigns|ad-groups|
            targets|search-terms|placements|negatives plus the drill scope
            (pages/ads/routing.ts). */}
        <Route path="/ads" element={<AdsHubPage />} />
        <Route path="/campaigns/new" element={<CampaignBuilderPage />} />
        {/* PR 5 — the old PPC paths (and /keywords, /budget-pacing) land on
            the hub's tab with their query kept. */}
        {LEGACY_ADS_ROUTES.map(r => (
          <Route key={r.path} path={r.path} element={<LegacyAdsRedirect />} />
        ))}
        {LEGACY_PATH_REDIRECTS.map(r => (
          <Route key={r.path} path={r.path} element={<LegacyAdsRedirect />} />
        ))}
      </>
    )
  }
  return (
    <>
      <Route path="/ads" element={<AdsHubPage />} />
      <Route path="/campaigns" element={<CampaignsPage />} />
      <Route path="/campaigns/new" element={<CampaignBuilderPage />} />
      <Route path="/ad-groups" element={<AdGroupsPage />} />
      {/* v2 #4: Keywords + Targets merged into one tabbed "Targets"
          shell (All / Keywords / ASINs). /keywords redirects onto the
          Keywords sub-tab so bookmarks + persisted sidebar configs land
          somewhere; KeywordsPage stays in the tree (embedded in the
          shell) but is no longer routed standalone. */}
      <Route path="/targets" element={<TargetsPage />} />
      <Route path="/keywords" element={<Navigate to="/targets?tab=keywords" replace />} />
      <Route path="/search-terms" element={<SearchTermsPage />} />
      <Route path="/placements" element={<PlacementsPage />} />
      <Route path="/negatives" element={<NegativesPage />} />
      {/* Budget Pacing page deleted (2026-07-01) — its data lives on
          /campaigns. Keep the redirect for stale bookmarks. */}
      <Route path="/budget-pacing" element={<Navigate to="/campaigns" replace />} />
      {LEGACY_PATH_REDIRECTS.map(r => (
        <Route key={r.path} path={r.path} element={<LegacyAdsRedirect />} />
      ))}
    </>
  )
}

/**
 * `location`: the location this pane renders — ALWAYS an object (UI overhaul
 * PR-1a). react-router 7 wraps the matched routes in a LocationContext.Provider
 * only when `<Routes location>` is given, so alternating between `undefined`
 * (the old active-pane value) and a snapshot changed the element type under
 * <Suspense> and remounted the whole page on every tab switch (audit APP-01).
 *   - the ACTIVE pane is handed the live `useLocation()` object, so normal
 *     in-page navigation (Link/useNavigate) keeps driving the real address bar;
 *   - an INACTIVE pane is handed a memoized snapshot of its own last location,
 *     so it keeps rendering whatever it last showed regardless of where the
 *     browser URL actually is.
 */
export default function AppRoutes({ location }: { location: Partial<Location> }) {
  return (
    <Suspense fallback={<PageLoading />}>
    <Routes location={location}>
      {/* WS1 (2026-06-15): Command Center is the home page at /overview
          (renamed from Dashboard). /dashboard redirects so stale
          bookmarks + persisted sidebar configs land somewhere. */}
      <Route path="/" element={<Navigate to="/overview" replace />} />
      <Route path="/overview" element={<CommandCenterPage />} />
      <Route path="/dashboard" element={<Navigate to="/overview" replace />} />

      {/* Operate → Performance subgroup: the Advertising hub and the PPC
          pages beside it (adsRoutes above). */}
      {adsRoutes()}
      {/* WS1: /product-ads is redundant with /products — redirect. The
          ProductAds page component stays in the tree (just unrouted). */}
      <Route path="/product-ads" element={<Navigate to="/products" replace />} />
      {/* PR 15: Advertising > Deals (sidebar item 'deals'). Read-only. */}
      <Route path="/deals" element={<DealsPage />} />
      {/* The PPC change trail. #16b reached it ONLY from a row's kebab "View
          history" (StateToggle.tsx), which builds the ?entity_type=&entity_id=
          query string this page reads; #36 added the sidebar entry ('history'
          in sidebarConfig.tsx) because a trail nobody can navigate to is a
          trail nobody reads. Unscoped (no query params) it shows the whole
          account. ?side=amazon opens the Amazon-side tab. */}
      <Route path="/history" element={<ChangeHistoryPage />} />
      <Route path="/suggestions" element={<SuggestionsPage />} />
      <Route path="/audit" element={<AuditPage />} />
      {/* SellerMate W3.3 — business alerts (?tab=active, ?status=). The
          header's Alerts pill links here; system health stays on /ops. */}
      <Route path="/alerts" element={<AlertsPage />} />
      {/* /asin is now folded into /products (merged page). Keep the
          redirect so stale bookmarks + sidebar configs land somewhere. */}
      <Route path="/asin" element={<Navigate to="/products" replace />} />
      <Route path="/products" element={<ProductsPage />} />
      <Route path="/products/:asin" element={<ProductDetailPage />} />
      {/* /skus was folded into /products as inline expandable child rows
          (2026-06-01). Redirect bookmarks so the legacy URL keeps working. */}
      <Route path="/skus" element={<Navigate to="/products" replace />} />
      <Route path="/hourly" element={<HourlyPage />} />
      {/* F3 (2026-06-26): the SP Keyword Hourly view + the PPC lens both
          fold into the consolidated /hourly tab's PPC subtab, where every
          hour expands to its keyword rows. Redirect preserves bookmarks. */}
      <Route path="/sp-keyword-hourly" element={<Navigate to="/hourly?channel=ppc" replace />} />

      {/* Operate (rest) */}
      {foldRoutes()}
      {/* AMC Audiences — dark-launched; AmcGate redirects to /overview
          unless CLICKPAY_AMC is on. Sidebar tab is hidden in lockstep. */}
      <Route path="/audiences" element={<AmcGate><AudiencesPage /></AmcGate>} />
      <Route path="/audiences/:id" element={<AmcGate><AudiencesPage /></AmcGate>} />
      {/* WS1 (2026-06-15): /reports was deprecated to the Command Center.
          SellerMate-upgrade W5.2: /reports now lands on the Ads Overview
          report. The old ReportsPage component stays unrouted. */}
      <Route path="/reports" element={<Navigate to="/reports/ads-overview" replace />} />
      <Route path="/reports/ads-overview" element={<AdsOverviewPage />} />

      {/* The workflow library (Phase 12 Build 37) */}
      <Route path="/workflows" element={<WorkflowsPage />} />

      {/* AI Layer */}
      <Route path="/agents" element={<AgentsPage />} />
      <Route path="/ai-assistant" element={<AIAssistantPage />} />

      {/* Rule Engine (Automations): see ruleEngineRoutes() above. */}
      {ruleEngineRoutes()}

      {/* Inventory — vendored MasterData ops pages (ClickPay Phase 4).
          Port-as-is; restyle is post-merge priority #1. All backend
          calls route through ops/lib/api.ts → /api/ops/*. */}
      <Route path="/inventory" element={<OpsLayout />}>
        {/* Marketplaces hub (2026-06-19): the unified per-marketplace
            ops hub is the inventory landing page. The standalone
            Products page was FULLY absorbed as its default sub-tab. */}
        <Route index element={<Navigate to="/inventory/marketplaces" replace />} />
        {/* /inventory/products (+ its ?tab=catalog|profitability|
            suppliers|linking) redirects into the matching Marketplaces
            sub-tab. RedirectProductsToMarketplaces maps the legacy
            sub-tab to the hub's id (?tab=); RestockHub's SUBTAB_ALIASES
            also resolves these so the surface lands correctly. */}
        <Route path="products" element={<RedirectProductsToMarketplaces />} />
        <Route path="products/:uid" element={<OpsProductDetailPage />} />
        {/* Product settings — /inventory/product-settings lists every product
            (?filter=hidden-everywhere|hidden-restock|… , ?q=); /<uid> opens
            that product's settings directly. */}
        <Route path="product-settings/:uid?" element={<OpsProductSettingsPage />} />
        {/* WS1: Suppliers folded into the Marketplaces → Orders tab as
            an inner section. Logistics is deprecated. Document Intake is
            restored — see its route + import below. */}
        <Route path="suppliers" element={<Navigate to="/inventory/marketplaces?tab=orders" replace />} />
        <Route path="logistics" element={<Navigate to="/inventory/marketplaces" replace />} />
        {/* Orders / Shipments / Payments folded into ONE tabbed shell.
            /inventory/orders is the shell (defaults to the Timeline
            sub-tab — the former Orders PO list). The old standalone
            Shipments / Payments routes redirect onto the matching
            sub-tab so deep links + bookmarks survive; the cross-page
            links into /inventory/orders?po=/?shipping_number=/?uid=
            still land on the Timeline body (default tab) with their
            query params intact. */}
        <Route path="orders" element={<OpsOrdersTabsPage />} />
        <Route path="shipments" element={<RedirectToOrdersTab tab="shipments" />} />
        <Route path="payments" element={<RedirectToOrdersTab tab="payments" />} />
        {/* Marketplaces hub — new canonical path. /restock-hub still
            redirects here (preserving ?mp=/?tab= deep-link params) so
            stale bookmarks + cross-page links keep working. */}
        <Route path="marketplaces" element={<OpsRestockHubPage />} />
        <Route path="restock-hub" element={<RedirectToMarketplaces />} />
        {/* Build 45 — one reorder surface. The standalone MPL Restock and
            Supplier Reorder pages were deleted; the hub does both jobs (its
            per-marketplace tabs ARE MPL Restock, its "all" tab IS Supplier
            Reorder). Neither path was wired into this router, so these are
            new redirects covering hand-typed URLs and old external links
            rather than replacements for live routes. ?mp=/?tab= params ride
            along. */}
        <Route path="mpl-restock" element={<RedirectToMarketplaces />} />
        <Route path="supplier-reorder" element={<RedirectToMarketplaces />} />
        {/* Document Receive — unified operator receive workspace.
            Round-4 Area A renamed /document-intake → /document-receive
            (combined-PDF split/classify/reconcile/file). Old path
            redirects so existing links keep working. Entry = the
            "Document Receive" button on the Orders tab strip. */}
        <Route path="document-receive" element={<OpsReceiveWorkspacePage />} />
        <Route path="document-receive/legacy" element={<OpsDocumentIntakePage />} />
        <Route path="document-intake" element={<Navigate to="/inventory/document-receive" replace />} />
        {/* Ops Health folded into the Ops Hub (task #12); the four
            monitoring tabs then merged into ONE System Health page
            (2026-07-03), now the admin System health tab of Settings
            (PR 7) — redirect onto it. */}
        <Route path="health" element={<Navigate to="/ops?tab=system" replace />} />
        {/* folder-map has no sidebar entry (same as MasterData) —
            DriveButton's missing-folder recovery flow navigates here. */}
        <Route path="folder-map" element={<OpsFolderMapPage />} />
      </Route>

      {/* Settings — ONE tabbed page at /ops (task #12, 2026-06-12; PR 7
          made it open on Users, with System health an admin tab). The old
          standalone routes redirect onto the matching tab so deep links +
          bookmarks survive. Internal links inside the folded pages (e.g.
          Ops' drill-downs, the header FreshnessBadge) still point at these
          paths and ride the redirects onto the right tab; old ?tab= values
          are rewritten by pages/settings/routing.ts. */}
      <Route path="/ops" element={<OpsHub />} />
      <Route path="/health" element={<Navigate to="/ops?tab=system" replace />} />
      <Route path="/freshness" element={<Navigate to="/ops?tab=system" replace />} />
      <Route path="/settings" element={<Navigate to="/ops" replace />} />
      <Route path="/settings/users" element={<Navigate to="/ops?tab=users" replace />} />
      <Route path="/settings/v6" element={<Navigate to="/ops?tab=advanced&section=engine" replace />} />
      <Route path="/settings/advanced" element={<Navigate to="/ops?tab=advanced" replace />} />
      {/* Goals folded into Products (task #13): per-row goal chip opens
          a drawer editor; bulk-select applies presets/buffers. The
          standalone Goals page is retired. */}
      <Route path="/goals" element={<Navigate to="/products" replace />} />

      {/* Anything the table above does not claim. Ranked last by
          react-router regardless of position, so it cannot shadow a real
          route. See ScreenNotFound's docblock for why a blank content area
          was the wrong answer. */}
      <Route path="*" element={<ScreenNotFound />} />
    </Routes>
    </Suspense>
  )
}
